import React, { useEffect, useRef, useState } from 'react';
import usePlaceholderTraceStore from './usePlaceholderTraceStore';
import getPlaceholderColors from '../../utils/placeholderColors';

const escapeAttr = (s) =>
  typeof window !== 'undefined' && window.CSS && typeof window.CSS.escape === 'function'
    ? window.CSS.escape(s)
    : String(s).replace(/(["\\])/g, '\\$1');

const PlaceholderTraceOverlay = () => {
  const activeKey = usePlaceholderTraceStore((s) => s.activeKey);
  const type = usePlaceholderTraceStore((s) => s.type);
  const containerRef = useRef(null);
  const [pairs, setPairs] = useState([]);

  useEffect(() => {
    if (!activeKey) {
      setPairs([]);
      return undefined;
    }

    let raf = 0;
    let lastSerialized = '';
    const escapedKey = escapeAttr(activeKey);
    const producerSelector =
      `[data-placeholder-key="${escapedKey}"][data-placeholder-role="output"]`;
    const consumerSelector =
      `[data-placeholder-key="${escapedKey}"][data-placeholder-role="reference"]`;

    const MIN_VISIBLE_PX = 6;
    const isChipVisible = (el, r) => {
      if (typeof el.checkVisibility === 'function') {
        if (
          !el.checkVisibility({
            contentVisibilityAuto: true,
            opacityProperty: true,
            visibilityProperty: true,
          })
        ) {
          return false;
        }
      } else if (el.offsetParent === null) {
        return false;
      }
      if (r.width < MIN_VISIBLE_PX || r.height < MIN_VISIBLE_PX) return false;
      return true;
    };

    const measure = () => {
      const container = containerRef.current;
      if (!container) {
        raf = requestAnimationFrame(measure);
        return;
      }
      const containerRect = container.getBoundingClientRect();

      const producerCenters = [];
      document.querySelectorAll(producerSelector).forEach((el) => {
        const r = el.getBoundingClientRect();
        if (!isChipVisible(el, r)) return;
        producerCenters.push({
          x: r.right - containerRect.left,
          y: r.top + r.height / 2 - containerRect.top,
        });
      });

      const consumerCenters = [];
      document.querySelectorAll(consumerSelector).forEach((el) => {
        const r = el.getBoundingClientRect();
        if (!isChipVisible(el, r)) return;
        consumerCenters.push({
          x: r.left - containerRect.left,
          y: r.top + r.height / 2 - containerRect.top,
        });
      });

      const next = [];
      for (const p of producerCenters) {
        for (const c of consumerCenters) {
          next.push({ p, c });
        }
      }
      const serialized = JSON.stringify(next);
      if (serialized !== lastSerialized) {
        lastSerialized = serialized;
        setPairs(next);
      }
      raf = requestAnimationFrame(measure);
    };
    raf = requestAnimationFrame(measure);
    return () => cancelAnimationFrame(raf);
  }, [activeKey]);

  if (!activeKey) return null;
  const color = type ? getPlaceholderColors(type).default : '#FF4A00';
  const escapedKey = escapeAttr(activeKey);
  const dimChipsCss =
    `.placeholder-trace-active [data-placeholder-key]:not([data-placeholder-key="${escapedKey}"]) {` +
    ` opacity: 0.35; transition: opacity 0.15s ease;` +
    ` }`;

  return (
    <div
      ref={containerRef}
      style={{
        position: 'absolute',
        inset: 0,
        pointerEvents: 'none',
        zIndex: 5,
      }}
    >
      <style>{dimChipsCss}</style>
      <svg
        width="100%"
        height="100%"
        style={{ overflow: 'visible', display: 'block' }}
      >
        {pairs.map(({ p, c }, i) => {
          const d = `M ${p.x} ${p.y} L ${c.x} ${c.y}`;
          return (
            <path
              key={i}
              d={d}
              fill="none"
              stroke={color}
              strokeWidth={1.5}
              strokeDasharray="6 4"
              opacity={0.85}
              style={{
                filter: `drop-shadow(0 0 3px ${color}66)`,
                animation: 'placeholder-trace-dash 0.8s linear infinite',
              }}
            />
          );
        })}
      </svg>
    </div>
  );
};

export default PlaceholderTraceOverlay;
